import React, { useState } from "react";
import { useLogger } from "../../../services/logging";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  LinearProgress,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  Timeline,
  BarChart,
  PieChart,
  Download,
  FilterList,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  useScenarios,
  useScenarioStats,
  useScenarioComparison,
} from "../../../hooks/useScenarios";
import {
  Scenario,
  ScenarioPerformance,
  PerformanceMetrics,
} from "../../../types/scenario";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`performance-tabpanel-${index}`}
      aria-labelledby={`performance-tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `performance-tab-${index}`,
    "aria-controls": `performance-tabpanel-${index}`,
  };
}

export function PerformanceDashboard() {
  const logger = useLogger("PerformanceDashboard");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [tabValue, setTabValue] = useState(0);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
    end: new Date(),
  });

  // Fetch real data using custom hooks
  const {
    scenarios,
    loading: scenariosLoading,
    error: scenariosError,
  } = useScenarios();
  const {
    stats,
    loading: statsLoading,
    error: statsError,
  } = useScenarioStats();
  const {
    comparison,
    loading: comparisonLoading,
    error: comparisonError,
    compareScenarios,
  } = useScenarioComparison();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleScenarioSelection = (scenarioId: string) => {
    setSelectedScenarios((prev) =>
      prev.includes(scenarioId)
        ? prev.filter((id) => id !== scenarioId)
        : [...prev, scenarioId],
    );
  };

  const handleCompareScenarios = async () => {
    if (selectedScenarios.length < 2) return;

    await compareScenarios(
      selectedScenarios,
      dateRange.start || undefined,
      dateRange.end || undefined,
    );
  };

  const handleExportData = () => {
    // TODO: Implement data export functionality
    logger.debug("Exporting performance data", { method: "exportData" });
  };

  const getPerformanceColor = (value: number, isPositive: boolean = true) => {
    if (isPositive) {
      return value > 0
        ? "success.main"
        : value < 0
          ? "error.main"
          : "text.secondary";
    }
    return value > 0
      ? "error.main"
      : value < 0
        ? "success.main"
        : "text.secondary";
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (statsLoading || scenariosLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Performance Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track and analyze your trading scenario performance
        </Typography>
      </Box>

      {/* Error Display */}
      {(scenariosError || statsError || comparisonError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {scenariosError || statsError || comparisonError}
        </Alert>
      )}

      {/* Overall Performance Metrics */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <TrendingUp sx={{ color: "success.main", mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Total Profit
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" color="success.main">
                  {formatCurrency(stats.totalProfit || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Net profit across all scenarios
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <TrendingDown sx={{ color: "error.main", mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Total Loss
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" color="error.main">
                  {formatCurrency(stats.totalLoss || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total losses across all scenarios
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Timeline sx={{ color: "info.main", mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Win Rate
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" color="info.main">
                  {formatPercentage(stats.averageWinRate || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average success rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <BarChart sx={{ color: "warning.main", mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Active Scenarios
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" color="warning.main">
                  {stats.activeScenarios || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Currently running strategies
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Main Content Tabs */}
      <Paper sx={{ width: "100%" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="performance tabs"
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
        >
          <Tab label="Overview" {...a11yProps(0)} />
          <Tab label="Scenario Performance" {...a11yProps(1)} />
          <Tab label="Comparison" {...a11yProps(2)} />
          <Tab label="Analytics" {...a11yProps(3)} />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Performance Trends
                  </Typography>
                  <Box
                    sx={{
                      height: 300,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      Chart component will be implemented here
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top Performers
                  </Typography>
                  {scenariosLoading ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : scenarios.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No scenarios found
                    </Typography>
                  ) : (
                    <Box>
                      {scenarios.slice(0, 5).map((scenario, index) => (
                        <Box key={scenario.id} sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="subtitle2">
                              {index + 1}. {scenario.name}
                            </Typography>
                            <Chip
                              label={scenario.category.replace("_", " ")}
                              size="small"
                              color="primary"
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {scenario.description}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Scenario Performance Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Individual Scenario Performance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Detailed performance metrics for each trading scenario
            </Typography>
          </Box>

          {scenariosLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : scenarios.length === 0 ? (
            <Box textAlign="center" p={3}>
              <Typography variant="body1" color="text.secondary">
                No scenarios found. Create some scenarios to see performance
                data.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Scenario</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Total Trades</TableCell>
                    <TableCell>Win Rate</TableCell>
                    <TableCell>Net Profit</TableCell>
                    <TableCell>Max Drawdown</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scenarios.map((scenario) => (
                    <TableRow key={scenario.id}>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {scenario.name}
                        </Typography>
                        {scenario.description && (
                          <Typography variant="body2" color="text.secondary">
                            {scenario.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={scenario.category.replace("_", " ")}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={scenario.isActive ? "Active" : "Inactive"}
                          color={scenario.isActive ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {scenario.performance?.totalTrades || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="success.main">
                          {scenario.performance?.winRate
                            ? formatPercentage(scenario.performance.winRate)
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={getPerformanceColor(
                            scenario.performance?.netProfit || 0,
                          )}
                        >
                          {scenario.performance?.netProfit
                            ? formatCurrency(scenario.performance.netProfit)
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="error.main">
                          {scenario.performance?.maxDrawdown
                            ? formatPercentage(scenario.performance.maxDrawdown)
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined">
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Comparison Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Scenario Comparison
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Compare performance across multiple scenarios
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Scenarios</InputLabel>
                <Select
                  multiple
                  value={selectedScenarios}
                  onChange={(e) =>
                    setSelectedScenarios(e.target.value as string[])
                  }
                  label="Select Scenarios"
                >
                  {scenarios.map((scenario) => (
                    <MenuItem key={scenario.id} value={scenario.id}>
                      {scenario.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={dateRange.start}
                  onChange={(date) =>
                    setDateRange((prev) => ({ ...prev, start: date }))
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={dateRange.end}
                  onChange={(date) =>
                    setDateRange((prev) => ({ ...prev, end: date }))
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              onClick={handleCompareScenarios}
              disabled={selectedScenarios.length < 2 || comparisonLoading}
              startIcon={
                comparisonLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <BarChart />
                )
              }
            >
              {comparisonLoading ? "Comparing..." : "Compare Scenarios"}
            </Button>
          </Box>

          {comparisonLoading && <LinearProgress sx={{ mb: 2 }} />}

          {comparison.length > 0 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Scenario</TableCell>
                    <TableCell>Total Trades</TableCell>
                    <TableCell>Win Rate</TableCell>
                    <TableCell>Net Profit</TableCell>
                    <TableCell>Sharpe Ratio</TableCell>
                    <TableCell>Max Drawdown</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparison.map((perf) => (
                    <TableRow key={perf.id}>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {scenarios.find((s) => s.id === perf.scenarioId)
                            ?.name || "Unknown"}
                        </Typography>
                      </TableCell>
                      <TableCell>{perf.totalTrades}</TableCell>
                      <TableCell color="success.main">
                        {formatPercentage(perf.winRate)}
                      </TableCell>
                      <TableCell color={getPerformanceColor(perf.netProfit)}>
                        {formatCurrency(perf.netProfit)}
                      </TableCell>
                      <TableCell>
                        {perf.sharpeRatio?.toFixed(2) || "N/A"}
                      </TableCell>
                      <TableCell color="error.main">
                        {formatPercentage(perf.maxDrawdown)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Performance Distribution
                  </Typography>
                  <Box
                    sx={{
                      height: 300,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      Pie chart will be implemented here
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Risk Metrics
                  </Typography>
                  <Box
                    sx={{
                      height: 300,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      Risk analysis chart will be implemented here
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Export Button */}
      <Box sx={{ mt: 3, textAlign: "right" }}>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleExportData}
        >
          Export Data
        </Button>
      </Box>
    </Container>
  );
}
